<template>
	<div class="portal-rail">
		<nav class="rail-navigation">
			<div class="navigation-group" v-for="group in navigationGroups" :key="group.key">
				<nav-link
					v-for="link in group.links"
					:key="link.route || link.hyperLink"
					:label="link.label"
					:icon="link.icon"
					:custom-icon="link.customIcon"
					:route="link.route"
					:alt-routes="(link.tabs || []).map((tab) => tab.route)"
					:hyper-link="link.hyperLink"
				></nav-link>
			</div>
		</nav>

		<v-menu offset-x nudge-left="8" content-class="workspace-menu" :close-on-content-click="false">
			<template v-slot:activator="{ on, attrs }">
				<button
					ref="clientContext"
					type="button"
					class="client-context"
					:class="{ 'is-collapsed': !expanded }"
					:aria-label="clientName"
					:content="clientName"
					v-bind="attrs"
					v-on="on"
					v-tippy="{
						placement: 'right',
						boundary: 'window',
					}"
				>
					<span class="client-avatar">{{ clientInitials }}</span>
					<span class="client-name" v-if="expanded">{{ clientName }}</span>
					<portal-icon
						v-if="expanded && hasMultipleClients"
						name="chevrons-up-down"
						:size="18"
						class="client-switch-icon"
					></portal-icon>
				</button>
			</template>
			<settings-nav></settings-nav>
		</v-menu>
	</div>
</template>

<script>
	import NavLink from '@/views/nav/NavLink.vue';
	import SettingsNav from '@/views/nav/settings/SettingsNav.vue';
	import NavMixin from '@/views/nav/NavMixin';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'LeftNav',

		components: { NavLink, SettingsNav, PortalIcon },

		mixins: [NavMixin],

		mounted() {
			this.syncClientTooltipState();
		},

		methods: {
			syncClientTooltipState() {
				this.$nextTick(() => {
					const button = this.$refs.clientContext;
					const tooltip = button && button._tippy;
					if (!tooltip) return;
					if (this.expanded) {
						tooltip.hide(0);
						tooltip.disable();
					} else {
						tooltip.enable();
					}
				});
			},
		},

		computed: {
			expanded() {
				return this.$store.state.leftNavExpanded;
			},

			navigationGroups() {
				const home = {
					label: this.$t('nav.home'),
					icon: 'home',
					route: '/welcome',
				};
				return [{ key: 'navigation', links: [home, ...this.navLinks] }];
			},

			clientName() {
				return this.$store.getters.getClientName || '';
			},

			hasMultipleClients() {
				const clients = this.$store.getters.getClientAccounts || [];
				return clients.length > 1;
			},
		},

		watch: {
			expanded() {
				this.syncClientTooltipState();
			},
		},
	};
</script>

<style lang="scss">
	.workspace-menu {
		width: min(304px, calc(100vw - 24px));
		max-height: calc(100vh - 32px);
		overflow: auto;
		padding: 8px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 18px) !important;
		background-color: var(--portal-surface);
		box-shadow: 0 20px 54px rgba(16, 17, 15, 0.2) !important;
	}
</style>

<style scoped lang="scss">
	.portal-rail {
		position: relative;
		width: 100%;
		min-width: 0;
		height: 100%;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		padding: 0 12px 12px;
		overflow: hidden;
	}

	.rail-navigation {
		min-height: 0;
		padding: 4px 2px 2px;
		overflow-x: hidden;
		overflow-y: auto;
		scrollbar-width: thin;
		scrollbar-color: color-mix(in srgb, var(--portal-nav-text) 28%, transparent) transparent;
	}

	.navigation-group {
		display: grid;
		gap: 2px;
	}

	.navigation-group + .navigation-group {
		margin-top: 2px;
	}

	.client-context {
		width: 100%;
		min-height: 50px;
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 14px;
		padding: 7px 8px;
		border: 0;
		border-radius: var(--brand-button-radius, 11px);
		background: var(--v-gray_20-base);
		background: color-mix(in srgb, currentColor 6%, transparent);
		color: var(--portal-nav-text);
		cursor: pointer;
		text-align: left;
		transition:
			background-color 160ms ease,
			color 160ms ease;
	}

	.client-context:hover {
		background: var(--v-gray_20-base);
		background: color-mix(in srgb, currentColor 10%, transparent);
	}

	.client-context:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.client-context.is-collapsed {
		min-height: 44px;
		justify-content: center;
		padding: 6px;
		border-radius: var(--brand-button-radius, 10px);
	}

	.client-avatar {
		width: 32px;
		height: 32px;
		flex: 0 0 32px;
		display: grid;
		place-items: center;
		border: 0;
		border-radius: 10px;
		background: var(--v-gray_20-base);
		background: color-mix(in srgb, currentColor 9%, transparent);
		color: currentColor;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.035em;
	}

	.client-name {
		min-width: 0;
		flex: 1;
		overflow: hidden;
		font-size: 13px;
		font-weight: 650;
		line-height: 1.25;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.client-switch-icon {
		flex: 0 0 auto;
		color: var(--portal-nav-muted);
	}

	@media (prefers-reduced-motion: reduce) {
		.client-context {
			transition: none;
		}
	}
</style>
